/**
 * Geschützte PDF-/Datei-Vorschau (/api/…, /export/…) mit Session-Cookie.
 */
import { sanitizeFileUrl } from "./filePreview.js";

const EXPORT_ERROR_MESSAGES = Object.freeze({
  export_failed_render:
    "Das PDF konnte nicht erstellt werden. Bitte Seite neu laden und erneut versuchen.",
  export_failed_template: "Die PDF-Vorlage fehlt in der Datenbank.",
  export_failed_template_invalid: "Die PDF-Vorlage ist ungültig.",
  CODE_GENERATION_FAILED: "Bestätigungscode konnte nicht erzeugt werden.",
});

export class PdfPreviewError extends Error {
  constructor(message, code = "") {
    super(message);
    this.name = "PdfPreviewError";
    this.code = code;
  }
}

export function isProtectedPreviewUrl(url = "") {
  const safe = sanitizeFileUrl(url);
  if (!safe) return false;
  return safe.startsWith("/api/") || safe.startsWith("/export/");
}

export function mapExportPreviewError(text = "", status = 0) {
  const key = String(text || "").trim();
  if (EXPORT_ERROR_MESSAGES[key]) return EXPORT_ERROR_MESSAGES[key];
  if (status === 401) return "Bitte melde dich erneut an.";
  if (status === 403) return "Keine Berechtigung für dieses PDF.";
  if (status === 404) return "PDF oder Bericht nicht gefunden.";
  if (status === 423) return "Bericht ist gesperrt – PDF ggf. nach Entsperren erneut erzeugen.";
  if (status >= 500) return "Serverfehler beim Erstellen des PDF.";
  if (key && key.length < 160 && !/[<>]/.test(key)) return key;
  return "PDF konnte nicht geladen werden.";
}

/** Chrome/Edge: eingebettete PDF-Viewer-Leiste (Dateiname, Zoom, Download …) */
export const PDF_EMBED_TOOLBAR_CLIP_PX = 56;

/** PDF Open Parameters – von manchen Viewern ignoriert, schadet nicht */
export const PDF_EMBED_VIEWER_PARAMS = "toolbar=0&navpanes=0&statusbar=0";

export function buildPdfIframeSrc(url = "") {
  const safe = String(url || "").trim();
  if (!safe) return "";
  const hash = PDF_EMBED_VIEWER_PARAMS;
  return safe.includes("#") ? `${safe}&${hash}` : `${safe}#${hash}`;
}

export function isExportPdfUrl(url = "") {
  const safe = sanitizeFileUrl(url);
  return Boolean(safe && safe.startsWith("/export/") && safe.includes(".pdf"));
}

/**
 * Lädt geschützte URLs und liefert Blob + Blob-URL für iframe/img/Share.
 * Validiert Export-PDFs anhand des %PDF-Magic-Bytes.
 */
export async function fetchProtectedPreviewAssets(url = "", { signal } = {}) {
  const safe = sanitizeFileUrl(url);
  if (!safe) return null;
  if (!isProtectedPreviewUrl(safe)) {
    return { blobUrl: safe, blob: null };
  }

  const res = await fetch(safe, {
    method: "GET",
    credentials: "include",
    signal,
  });

  const contentType = String(res.headers.get("content-type") || "").toLowerCase();

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new PdfPreviewError(mapExportPreviewError(text, res.status), text.trim());
  }

  const buffer = await res.arrayBuffer();
  const header = new Uint8Array(buffer).slice(0, 4);

  if (safe.startsWith("/export/")) {
    const looksLikePdf =
      header[0] === 0x25 &&
      header[1] === 0x50 &&
      header[2] === 0x44 &&
      header[3] === 0x46;
    if (!looksLikePdf) {
      const text = new TextDecoder().decode(buffer).trim();
      throw new PdfPreviewError(mapExportPreviewError(text, res.status), text.slice(0, 80));
    }
  }

  const blobType = contentType.includes("pdf") ? "application/pdf" : undefined;
  const blob = new Blob([buffer], { type: blobType || "application/octet-stream" });
  return { blob, blobUrl: URL.createObjectURL(blob) };
}

/**
 * Lädt geschützte URLs und liefert eine Blob-URL für iframe/img.
 * Validiert Export-PDFs anhand des %PDF-Magic-Bytes.
 */
export async function fetchProtectedPreviewUrl(url = "", { signal } = {}) {
  const assets = await fetchProtectedPreviewAssets(url, { signal });
  return assets?.blobUrl ?? null;
}
